import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { isLicenseRequired } from '../../lib/licenseStatus';
import { useOnboardingOptional } from '../onboarding/OnboardingContext';
import {
  fetchMyOptInPrompts,
  markOptInPromptShown,
  MY_OPT_IN_PROMPTS_QUERY_KEY,
  type MyOptInPrompt,
} from '../../services/optInPromptsService';

/**
 * Automatic opt-in prompt (e.g. the Instagram campaign) for the signed-in app.
 *
 *  - `requestAutoStart()` — called by every AppLayout mount (OptInPromptAutoStart).
 *    The first call after sign-in loads the user's prompts; the first one that is
 *    due opens once the onboarding tour is out of the way, and the API records that
 *    it was shown for this version (it never opens on its own again for it).
 *  - `busy` — true until that decision is made and while the dialog is open: the
 *    feedback prompt waits for it, so two modals never open at once.
 *  - Answers can always be given, changed or withdrawn in Settings → Einwilligungen.
 *  - Never auto-opens while the licence notice is shown or in an automated browser.
 */
export interface OptInPromptContextValue {
  /** The prompt shown in the dialog, null when closed. */
  prompt: MyOptInPrompt | null;
  close: () => void;
  busy: boolean;
  requestAutoStart: () => void;
}

export const OptInPromptContext = createContext<OptInPromptContextValue | null>(null);

function isAutomatedBrowser(): boolean {
  try {
    return typeof navigator !== 'undefined' && navigator.webdriver === true;
  } catch {
    return false;
  }
}

export function OptInPromptProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const isAuthenticated = auth.state?.isAuthenticated === true;
  const onboarding = useOnboardingOptional();
  const onboardingBusy = onboarding !== null && (!onboarding.autoStartSettled || onboarding.isOpen);
  const [pending, setPending] = useState<MyOptInPrompt | null>(null);
  const [prompt, setPrompt] = useState<MyOptInPrompt | null>(null);
  const [settled, setSettled] = useState(false);
  const attemptedRef = useRef(false);
  const seqRef = useRef(0);

  // A sign-out ends the session: close the dialog and decide again after the next sign-in.
  useEffect(() => {
    if (isAuthenticated) return;
    attemptedRef.current = false;
    seqRef.current += 1;
    setPending(null);
    setPrompt(null);
    setSettled(false);
  }, [isAuthenticated]);

  const requestAutoStart = useCallback(() => {
    if (attemptedRef.current || !isAuthenticated) return;
    attemptedRef.current = true;
    if (isAutomatedBrowser() || isLicenseRequired()) {
      setSettled(true);
      return;
    }
    const seq = ++seqRef.current;
    void fetchMyOptInPrompts()
      .then((prompts) => {
        if (seq !== seqRef.current) return;
        queryClient.setQueryData(MY_OPT_IN_PROMPTS_QUERY_KEY, prompts);
        const due = prompts.find((p) => p.due) ?? null;
        if (due) setPending(due);
        else setSettled(true);
      })
      .catch(() => {
        if (seq === seqRef.current) setSettled(true);
      });
  }, [isAuthenticated, queryClient]);

  // The due prompt waits until the onboarding tour's automatic start is decided and the tour is closed.
  useEffect(() => {
    if (!pending || onboardingBusy || prompt) return;
    setPrompt(pending);
    setPending(null);
    void markOptInPromptShown(pending.id).catch(() => undefined);
  }, [pending, onboardingBusy, prompt]);

  const close = useCallback(() => {
    setPrompt(null);
    setSettled(true);
  }, []);

  const busy = !settled || pending !== null || prompt !== null;

  const value = useMemo<OptInPromptContextValue>(
    () => ({ prompt, close, busy, requestAutoStart }),
    [prompt, close, busy, requestAutoStart],
  );

  return <OptInPromptContext.Provider value={value}>{children}</OptInPromptContext.Provider>;
}

/** The opt-in prompt state, or null outside an OptInPromptProvider (e.g. isolated page tests). */
export function useOptInPromptOptional(): OptInPromptContextValue | null {
  return useContext(OptInPromptContext);
}

/**
 * Rendered by AppLayout: landing in the signed-in app shell is the signal that a
 * due opt-in prompt may open automatically. No-op without a provider.
 */
export function OptInPromptAutoStart() {
  const requestAutoStart = useOptInPromptOptional()?.requestAutoStart;
  useEffect(() => {
    requestAutoStart?.();
  }, [requestAutoStart]);
  return null;
}
